import { ReportTemplateEntityType, type ReportBuilderLastGraphStateQuery } from '@/graphql/types';
import { isBefore } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { startOfOmnidots } from '../../../utils/date-fns';
import type { GraphTimeWindow } from './elements/types';
import type { PreviewSelection } from './previewContextFactory';
import type { PreviewMeasuringPointModel, PreviewProjectModel } from './previewContextModels';

export interface PreviewOptions {
    entityType: ReportTemplateEntityType;
    projects: readonly PreviewProjectModel[];
    measuringPoints: readonly PreviewMeasuringPointModel[];
}

export function previewRange(
    start: unknown,
    end: unknown,
    timezone: string
): GraphTimeWindow | null {
    if (
        typeof start !== 'number' ||
        typeof end !== 'number' ||
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        !Number.isFinite(new Date(start).getTime()) ||
        !Number.isFinite(new Date(end).getTime()) ||
        start >= end ||
        isBefore(utcToZonedTime(start, timezone), startOfOmnidots)
    ) {
        return null;
    }
    return { viewStartTs: start, viewEndTs: end };
}

export function createUnsupportedPreviewSelectionEntityTypeError(
    entityType: ReportTemplateEntityType
): Error {
    return new Error(`Unsupported preview selection entity type "${String(entityType)}".`);
}

function previewSelectionParam(entityType: ReportTemplateEntityType): string {
    if (entityType === ReportTemplateEntityType.Project) return 'previewProjectId';
    if (entityType === ReportTemplateEntityType.MeasuringPoint) return 'previewMeasuringPointId';
    throw createUnsupportedPreviewSelectionEntityTypeError(entityType);
}

/** Returns the URL selection for a known ID; unknown or missing IDs return null. */
export function readPreviewSelection(
    { entityType, projects, measuringPoints }: PreviewOptions,
    params: URLSearchParams
): PreviewSelection | null {
    const id = params.get(previewSelectionParam(entityType));
    if (entityType === ReportTemplateEntityType.Project) {
        const project = projects.find((candidate) => candidate.id === id);
        return project ? { projectId: project.id } : null;
    }
    const point = measuringPoints.find((candidate) => candidate.id === id);
    return point ? { measuringPointId: point.id, projectId: point.projectId ?? undefined } : null;
}

export function readPreviewRange(
    params: URLSearchParams,
    timezone: string
): GraphTimeWindow | null {
    const start = params.get('previewStartTs');
    const end = params.get('previewEndTs');
    return start?.trim() && end?.trim() ? previewRange(Number(start), Number(end), timezone) : null;
}

export function writePreviewSelection(
    params: URLSearchParams,
    entityType: ReportTemplateEntityType,
    id: string
): URLSearchParams {
    const next = new URLSearchParams(params);
    next.set(previewSelectionParam(entityType), id);
    return next;
}

export function writePreviewRange(
    params: URLSearchParams,
    { viewStartTs, viewEndTs }: GraphTimeWindow,
    timezone: string
): URLSearchParams {
    if (!previewRange(viewStartTs, viewEndTs, timezone)) {
        throw new Error('Preview range must start on or after the minimum date and before end.');
    }
    const next = new URLSearchParams(params);
    next.set('previewStartTs', String(viewStartTs));
    next.set('previewEndTs', String(viewEndTs));
    return next;
}

export type SavedGraphState = Exclude<
    ReportBuilderLastGraphStateQuery['lastGraphState'],
    undefined
>;

/** Validates the graph-state response; `null` is valid and means no saved state. */
export function requireSavedGraphState(
    data: ReportBuilderLastGraphStateQuery | undefined
): SavedGraphState {
    if (!data) {
        throw new Error('Missing preview graph state query result.');
    }
    const saved = data.lastGraphState;
    if (saved === undefined) {
        throw new Error('Missing lastGraphState field in preview query result.');
    }
    if (saved && (saved.startTime === undefined || saved.endTime === undefined)) {
        throw new Error('Missing saved graph date fields in preview query result.');
    }
    return saved;
}

/** Default context for a missing URL choice: the saved point/project, then the first option. */
export function resolvePreviewDefaultSelection(
    { entityType, projects, measuringPoints }: PreviewOptions,
    saved: SavedGraphState
): PreviewSelection | null {
    const savedPoint = saved?.measuringPointIds
        .map((id) => measuringPoints.find((point) => point.id === id))
        .find((point) => point !== undefined);
    const isProject = entityType === ReportTemplateEntityType.Project;
    const ids = (isProject ? projects : measuringPoints).map(({ id }) => id);
    const savedId = isProject ? savedPoint?.projectId : savedPoint?.id;
    const selectionId = ids.find((id) => id === savedId) ?? ids[0];
    if (!selectionId) return null;
    return isProject ? { projectId: selectionId } : { measuringPointId: selectionId };
}

/** Default dates for the resolved context: the saved range, then the last seven days. */
export function resolvePreviewDefaultRange(
    saved: SavedGraphState,
    now: number,
    timezone: string
): GraphTimeWindow {
    return (
        previewRange(saved?.startTime, saved?.endTime, timezone) ?? {
            viewStartTs: now - 7 * 24 * 60 * 60 * 1000,
            viewEndTs: now,
        }
    );
}
